import { useTranslation } from "react-i18next";
import type { AgentStepDefinition } from "../../../api/content/authoring/stepDecoders";
import type { AuthoringOptions } from "../draft/authoringOptions";
import FieldProblems from "../draft/FieldProblems";
import type { DefinitionCheck } from "../draft/useDefinitionCheck";
import AgentStepBehaviour from "./AgentStepBehaviour";
import Section from "../../../ui/forms/Section";
import type { PromptDraft } from "./promptDraft";
import PromptFields from "./PromptFields";
import SourceEditor from "./SourceEditor";
import { placeholderNames, withPlaceholderSource, withPlaceholdersOf } from "./stepDefinitions";
import EmptyState from "../../../ui/feedback/EmptyState";

interface AgentStepFieldsProps {
  step: AgentStepDefinition;
  prompt: PromptDraft;
  /** The step's own agent instructions; `null` for none (WD-1+3 D-2). */
  instructions: PromptDraft | null;
  options: AuthoringOptions;
  check: DefinitionCheck;
  onChange: (step: AgentStepDefinition, prompt: PromptDraft) => void;
  onInstructions: (instructions: PromptDraft | null) => void;
}

/**
 * An agent step (AU-4): a task prompt whose placeholders are each wired to one source, the
 * step's own agent instructions if any, and what the agent may use and must deliver.
 */
export default function AgentStepFields({ step, prompt, instructions, options, check, onChange, onInstructions }: AgentStepFieldsProps) {
  const { t } = useTranslation();
  // An existing prompt's placeholders were read when it was chosen; a new one's follow its text.
  const names = prompt.kind === "new" ? placeholderNames(prompt.text) : Object.keys(step.placeholders);
  return (
    <>
      <PromptFields
        title={t("authoring.step.agent.prompt")}
        hint={t("authoring.step.agent.promptHint")}
        path="prompt"
        draft={prompt}
        optional={false}
        defaultName={step.name}
        options={options}
        check={check}
        onChange={(next, text) => {
          if (next === null) return;
          // The task's placeholders become the step's rows; unchanged or unread text leaves them as they are.
          const pinned = { ...step, prompt: next.kind === "existing" ? next.reference : "" };
          onChange(text === null ? pinned : withPlaceholdersOf(pinned, text), next);
        }}
      />
      <PromptFields
        title={t("authoring.step.agent.instructions")}
        hint={t("authoring.step.agent.instructionsHint")}
        path="agent_instructions"
        draft={instructions}
        optional={true}
        defaultName={t("authoring.step.agent.instructionsName", { name: step.name })}
        options={options}
        check={check}
        onChange={(next) => { onInstructions(next); }}
      />
      <Section title={t("authoring.step.agent.placeholders")} hint={t("authoring.step.agent.placeholdersHint")}>
        {names.length === 0 ? <EmptyState size="small">{t("authoring.step.agent.noPlaceholders")}</EmptyState> : null}
        <ul className="space-y-3">
          {names.map((name) => (
            <li key={name} className="space-y-1">
              <p className="font-mono text-sm font-semibold">{`{${name}}`}</p>
              <SourceEditor
                definition={step}
                label={`{${name}}`}
                slotName={name}
                source={step.placeholders[name] ?? null}
                slot={(() => {
                  const source = step.placeholders[name];
                  return source?.source === "input" ? (step.inputs[source.input] ?? null) : null;
                })()}
                options={options}
                accepts={null}
                optional={false}
                onChange={(source, slot) => {
                  if (source !== null) {
                    onChange(withPlaceholderSource(step, name, source, slot), prompt);
                  }
                }}
              />
              <FieldProblems check={check} path={`placeholders.${name}`} />
            </li>
          ))}
        </ul>
      </Section>
      <AgentStepBehaviour step={step} names={names} options={options} check={check} onChange={(next) => { onChange(next, prompt); }} />
    </>
  );
}
